import { useRef, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Download } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import useChartExport from '@/hooks/useChartExport';
import { isChartHidden, chartTypeOverride, CHART_TYPE_SWITCHABLE } from '@/dashboards/dashboardSections';
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from 'recharts';

const TYPE_PALETTE = ['#A5D62C', '#1B9CEA', '#F59E0B', '#A855F7', '#EC4899', '#14B8A6', '#3B82F6', '#10B981'];
const TOOLTIP_STYLE = { background: '#1F2833', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 11, color: '#fff' };

/**
 * Renderiza chartData ([{name, value}]) según el tipo elegido por el usuario.
 * Solo se llama cuando ChartCard recibe chartData Y el chartId está en
 * CHART_TYPE_SWITCHABLE (dashboardSections.js) -- el resto de los gráficos
 * (series múltiples, stacked, scatter, composed) ignora esto y usa `children`
 * tal cual, sin cambio de tipo todavía.
 */
function renderByType(type, chartData, height) {
  if (type === 'table') {
    return (
      <div className="overflow-y-auto text-xs" style={{ maxHeight: height }}>
        <table className="w-full">
          <thead>
            <tr className="text-left text-muted-foreground border-b border-white/5">
              <th className="py-1.5 px-2 font-normal">Nombre</th>
              <th className="py-1.5 px-2 font-normal text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            {chartData.map((d, i) => (
              <tr key={i} className="border-b border-white/[0.02]">
                <td className="py-1.5 px-2 text-white">{d.name}</td>
                <td className="py-1.5 px-2 text-right text-cyan-300">{d.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (type === 'pie' || type === 'doughnut') {
    const innerRadius = type === 'doughnut' ? 50 : 0;
    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={chartData} dataKey="value" nameKey="name" innerRadius={innerRadius} outerRadius={Math.min(90, height / 2 - 10)} paddingAngle={2} label={{ fill: '#cbd5e1', fontSize: 10 }}>
            {chartData.map((_, i) => <Cell key={i} fill={TYPE_PALETTE[i % TYPE_PALETTE.length]} />)}
          </Pie>
          <Tooltip contentStyle={TOOLTIP_STYLE} />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  // bar (default)
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
        <XAxis dataKey="name" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
        <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
        <Tooltip contentStyle={TOOLTIP_STYLE} />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {chartData.map((_, i) => <Cell key={i} fill={TYPE_PALETTE[i % TYPE_PALETTE.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Wrapper estándar para gráficos en dashboards v2.
 *
 * Props:
 *  - title, subtitle?, icon?, actions?, loading?, empty?, emptyMessage?, height?, testid?
 *  - downloadable?: bool (default false) — muestra "Descargar PDF" (por-gráfico).
 *  - chartTitle?: string — título del PDF/archivo si downloadable=true (default: title).
 *  - tabKey?: string — key del tab dueño (dashboardSections.js), requerido para
 *    poder ocultarse/cambiar de tipo vía preferencias del usuario.
 *  - chartId?: string — id estable (mismo que data-testid) para ocultar/tipo.
 *    Sin chartId, el gráfico ignora las preferencias de personalización (no se
 *    puede ocultar ni cambiar de tipo, pero sigue funcionando igual que antes).
 *  - chartData?: [{name, value}] — si se da Y chartId está en CHART_TYPE_SWITCHABLE,
 *    permite que el usuario cambie bar/pie/doughnut/table; si no, `children` manda.
 *  - children: el gráfico recharts u otro
 */
export default function ChartCard({
  title,
  subtitle,
  icon: Icon,
  actions,
  loading = false,
  empty = false,
  emptyMessage = 'Sin datos para el rango seleccionado',
  height = 280,
  testid,
  downloadable = false,
  chartTitle,
  tabKey,
  chartId,
  chartData,
  children,
}) {
  const chartNodeRef = useRef(null);
  const { user } = useAuth();
  const { exportChart, exporting } = useChartExport();

  const hiddenByPrefs = useMemo(
    () => Boolean(chartId && tabKey && isChartHidden(user?.dashboard_prefs, tabKey, chartId)),
    [chartId, tabKey, user?.dashboard_prefs],
  );

  const handleDownload = useCallback(() => {
    exportChart(
      chartNodeRef,
      chartTitle || title,
      user?.company_name || user?.company?.name,
      user?.name,
    );
  }, [exportChart, chartTitle, title, user]);

  if (hiddenByPrefs) return null;

  const showDownload = downloadable && !loading && !empty;
  const typeOverride = chartId ? chartTypeOverride(user?.dashboard_prefs, chartId) : null;
  const canSwitchType = Boolean(chartId && chartData && CHART_TYPE_SWITCHABLE.has(chartId));
  const content = (!loading && !empty && canSwitchType && typeOverride)
    ? renderByType(typeOverride, chartData, height)
    : children;

  return (
    <Card className="bg-card/50 backdrop-blur-md border-white/5 min-w-0" data-testid={testid || `chart-${title}`}>
      <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0 gap-2">
        <div className="min-w-0">
          <CardTitle className="text-sm font-heading text-white flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-cyan-400" />}
            {title}
          </CardTitle>
          {subtitle && <p className="text-[10px] text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {actions}
          {showDownload && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={exporting}
              title="Descargar gráfico como PDF"
              data-testid={`${testid || title}-download-pdf`}
              className="flex items-center gap-1 h-7 px-2 rounded-md bg-black/20 border border-white/10 text-[10px] text-muted-foreground hover:text-white hover:bg-black/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              <span className="hidden lg:inline">PDF</span>
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent style={{ minHeight: height }}>
        {loading ? (
          <div className="flex items-center justify-center" style={{ height }}>
            <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
          </div>
        ) : empty ? (
          <div className="flex items-center justify-center text-xs text-muted-foreground" style={{ height }}>
            {emptyMessage}
          </div>
        ) : downloadable ? (
          <div ref={chartNodeRef}>{content}</div>
        ) : (
          content
        )}
      </CardContent>
    </Card>
  );
}
